import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { canSeeFinancialFields } from './utils/jobUtils';

/**
 * Financial fields component for payout and billing information
 * @param {Object} props - Component props
 * @param {Object} props.formData - Current form data
 * @param {Function} props.onChange - Form data change handler
 * @param {string} props.serviceTypeId - Selected service type ID
 * @param {Array} props.serviceTypes - Available service types
 * @param {Object} props.selectedProperty - Selected property with default financial values
 * @param {Object} props.errors - Validation errors
 * @returns {JSX.Element|null} FinancialFields component or null if not visible
 */
export default function FinancialFields({ 
  formData, 
  onChange, 
  serviceTypeId, 
  serviceTypes, 
  selectedProperty, 
  errors = {} 
}) {
  const { user } = useAuth();
  const [showFinancialFields, setShowFinancialFields] = useState(false);

  // Check if user can see financial fields
  const canSeeFields = canSeeFinancialFields(user, serviceTypeId, serviceTypes);

  if (!canSeeFields) {
    return null;
  }

  // Find the selected service type
  const selectedServiceType = serviceTypes?.find(st => st.id === serviceTypeId);

  // Helper to check if we should show property financial defaults
  const shouldShowPropertyDefaults = () => {
    return formData.category === 'CLEANING' || selectedServiceType?.usePropertyFinancialFields === true;
  };

  const handleChange = (field, value) => {
    onChange({
      ...formData,
      [field]: value
    });
  };

  const useDefaultPayout = () => {
    if (selectedProperty?.payout) {
      const payoutValue = typeof selectedProperty.payout === 'number' ? 
        selectedProperty.payout : Number(selectedProperty.payout);
      handleChange('payout', payoutValue.toString());
    }
  };

  const useDefaultBilledAmount = () => {
    if (selectedProperty?.price) {
      const priceValue = typeof selectedProperty.price === 'number' ? 
        selectedProperty.price : Number(selectedProperty.price);
      handleChange('billedAmount', priceValue.toString());
    }
  };

  return (
    <div className="mb-4 border-t border-gray-200 pt-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Financial Information</span>
        <button
          type="button"
          onClick={() => setShowFinancialFields(!showFinancialFields)}
          className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {showFinancialFields ? 'Hide Financial Fields' : 'Show Financial Fields'}
        </button>
      </div>
      
      {showFinancialFields && (
        <div className="mt-3 space-y-3">
          <div>
            <div className="flex items-center justify-between">
              <label htmlFor="payout" className="block text-sm font-medium text-gray-700">
                Payout Amount ($)
              </label>
              {shouldShowPropertyDefaults() && selectedProperty?.payout && (
                <span className="text-xs text-gray-500">
                  Property default: ${typeof selectedProperty.payout === 'number' ? 
                    selectedProperty.payout.toFixed(2) : 
                    Number(selectedProperty.payout).toFixed(2)}
                </span>
              )}
            </div>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                name="payout"
                id="payout"
                value={formData.payout || ''}
                onChange={(e) => handleChange('payout', e.target.value)}
                className={`mt-1 block w-full border p-2 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                  errors.payout ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="0.00"
              />
              {shouldShowPropertyDefaults() && selectedProperty?.payout && (
                <button 
                  type="button"
                  onClick={useDefaultPayout}
                  className="absolute right-2 top-2.5 text-xs text-blue-600 hover:text-blue-800"
                >
                  Use default
                </button>
              )}
            </div>
            {errors.payout && (
              <p className="mt-1 text-xs text-red-500">{errors.payout}</p>
            )}
          </div>
          
          <div>
            <div className="flex items-center justify-between">
              <label htmlFor="billedAmount" className="block text-sm font-medium text-gray-700">
                Billed Amount ($)
              </label>
              {shouldShowPropertyDefaults() && selectedProperty?.price && (
                <span className="text-xs text-gray-500">
                  Property default: ${typeof selectedProperty.price === 'number' ? 
                    selectedProperty.price.toFixed(2) : 
                    Number(selectedProperty.price).toFixed(2)}
                </span>
              )}
            </div>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                name="billedAmount"
                id="billedAmount"
                value={formData.billedAmount || ''}
                onChange={(e) => handleChange('billedAmount', e.target.value)}
                className={`mt-1 block w-full border p-2 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                  errors.billedAmount ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="0.00"
              />
              {shouldShowPropertyDefaults() && selectedProperty?.price && (
                <button 
                  type="button"
                  onClick={useDefaultBilledAmount}
                  className="absolute right-2 top-2.5 text-xs text-blue-600 hover:text-blue-800"
                >
                  Use default
                </button>
              )}
            </div>
            {errors.billedAmount && (
              <p className="mt-1 text-xs text-red-500">{errors.billedAmount}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
} 